import React, { useState, useEffect, useContext, useRef } from "react";
import "./baseScheduleModal.css";
import translations from "../../../translations/translations";
import { CombinedContext } from "../../../context/combinedContext";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import { ChevronDown, Trash2 } from "lucide-react";
import StaffDropdown from "../../common/StaffDropdown/StaffDropdown";
import dayjs from "dayjs";
import useModalDismiss from "../../common/useModalDismiss";

const defaultDays = [
	{ dayEN: "Monday", dayES: "Lunes", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Tuesday", dayES: "Martes", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Wednesday", dayES: "Miércoles", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Thursday", dayES: "Jueves", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Friday", dayES: "Viernes", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Saturday", dayES: "Sábado", start: "09:00", end: "17:00", open: false, breaks: [] },
	{ dayEN: "Sunday", dayES: "Domingo", start: "09:00", end: "17:00", open: false, breaks: [] },
];

const SLOT_MINUTES = 30;

const toMin = (t) => {
	if (!t) return 0;
	const [h, m] = t.split(":").map(Number);
	return h * 60 + m;
};

const toTime = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

// A break has to sit strictly inside the day's hours — one touching an edge is
// really just a shorter shift — so the usable window is a slot in from each end.
const breakWindow = (day) => ({ from: toMin(day.start) + SLOT_MINUTES, to: toMin(day.end) - SLOT_MINUTES });

// Where a new break belongs on THIS day: the usual lunch hour when the day
// covers it, otherwise the middle of whatever hours the day actually has. A
// fixed 12:00–13:00 falls outside an afternoon shift, and the editor then
// refuses every correction because the break starts before the shift does.
const nextBreakSlot = (day) => {
	const { from, to } = breakWindow(day);
	const taken = (day.breaks || []).map((b) => [toMin(b.start), toMin(b.end)]);

	// an hour if the day can spare it, otherwise the shortest break we can offer
	for (const duration of [60, SLOT_MINUTES]) {
		if (to - from < duration) continue;

		const midday = 12 * 60;
		const centred = Math.round((toMin(day.start) + toMin(day.end) - duration) / 2 / SLOT_MINUTES) * SLOT_MINUTES;
		const preferred =
			midday >= from && midday + duration <= to ? midday : Math.min(Math.max(centred, from), to - duration);

		// walk outward from the preferred start so a second break lands beside the
		// first instead of on top of it, leaning later when both sides are equal
		const starts = [];
		for (let s = from; s + duration <= to; s += SLOT_MINUTES) starts.push(s);
		starts.sort((a, b) => Math.abs(a - preferred) - Math.abs(b - preferred) || b - a);

		const free = starts.find((s) => !taken.some(([bs, be]) => s < be && s + duration > bs));
		if (free !== undefined) return { start: toTime(free), end: toTime(free + duration) };
	}

	return null; // the day is too short to hold a break
};

// Keeps a day's breaks inside its hours: moves what still fits, drops what
// can't. Runs whenever hours change and whenever a schedule loads, so a break
// saved against yesterday's hours never strands outside the working window.
const fitBreaksToDay = (day) => {
	const { from, to } = breakWindow(day);
	let cursor = from;

	const breaks = [...(day.breaks || [])]
		.sort((a, b) => toMin(a.start) - toMin(b.start))
		.reduce((kept, b) => {
			const duration = Math.min(toMin(b.end) - toMin(b.start), to - cursor);
			if (duration < SLOT_MINUTES) return kept;

			const start = Math.min(Math.max(toMin(b.start), cursor), to - duration);
			cursor = start + duration;

			return [...kept, { ...b, start: toTime(start), end: toTime(start + duration) }];
		}, []);

	return { ...day, breaks };
};

const BaseScheduleModal = ({ onClose, staffList, initialStaffId, onStaffChange, token }) => {
	useModalDismiss(onClose);
	const { language, userData, setUserData } = useContext(CombinedContext);
	// const scheduleId = userData?.user?.scheduleId;

	const role = userData?.user?.role;

	const [days, setDays] = useState(defaultDays);
	const [expandedDay, setExpandedDay] = useState(null);
	const [saving, setSaving] = useState(false);
	const [selectedStaffId, setSelectedStaffId] = useState(null);
	const [selectedScheduleId, setSelectedScheduleId] = useState(null);
	const [currentSchedule, setCurrentSchedule] = useState(null);
	const [loadingSchedule, setLoadingSchedule] = useState(false);
	const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);

	const hasInitialized = useRef(false);
	const scheduleEditsRef = useRef({}); // { [scheduleId]: days[] } — pending edits per staff, kept across staff switches

	// Only picks a staff member the first time — after that the selection belongs
	// to the user, so a save / week reload never drags them back to the default.
	useEffect(() => {
		if (staffList.length > 0 && !hasInitialized.current) {
			hasInitialized.current = true;
			const target = initialStaffId ? staffList.find((s) => s.staffId === initialStaffId) : staffList[0];
			const first = target || staffList[0];
			setSelectedStaffId(first.staffId);
			setSelectedScheduleId(first.scheduleId);
			// let the calendar remember who we're on, so reopening lands here too
			if (onStaffChange) onStaffChange(first.staffId);
			loadSchedule(first);
		}
	}, [staffList]);

	useEffect(() => {
		if (!currentSchedule?.days?.length) return;

		const loaded = defaultDays.map((d) => {
			const found = currentSchedule.days.find((x) => x.dayEN === d.dayEN);
			return found
				? fitBreaksToDay({
						dayEN: found.dayEN,
						dayES: found.dayES,
						open: true,
						start: found.shifts?.[0]?.start || d.start,
						end: found.shifts?.[0]?.end || d.end,
						breaks: found.breaks || [],
					})
				: d;
		});

		setDays(loaded);
	}, [currentSchedule]);

	const times = Array.from({ length: 48 }, (_, i) => {
		const h = Math.floor(i / 2);
		const m = i % 2 === 0 ? "00" : "30";
		return `${String(h).padStart(2, "0")}:${m}`;
	});

	const toggleOpen = (index) => {
		const updated = [...days];
		updated[index].open = !updated[index].open;

		// auto-collapse if closing the day
		if (!updated[index].open && expandedDay === updated[index].dayEN) {
			setExpandedDay(null);
		}

		setDays(updated);
	};

	const handleTimeChange = (i, field, value) => {
		setDays((prev) =>
			prev.map((d, idx) => {
				if (idx !== i) return d;

				const day = { ...d, [field]: value };

				// the start select offers every time of day, so it can be pushed past
				// the end — carry the end along rather than leave an impossible day
				if (field === "start" && toMin(value) >= toMin(day.end)) {
					day.end = toTime(Math.min(toMin(value) + 60, 24 * 60 - SLOT_MINUTES));
				}

				// the hours moved, so the breaks move with them
				return fitBreaksToDay(day);
			})
		);
	};

	const addBreak = (i) => {
		setDays((prev) =>
			prev.map((d, idx) => {
				if (idx !== i) return d;

				const slot = nextBreakSlot(d);
				return slot ? { ...d, breaks: [...d.breaks, slot] } : d;
			})
		);
	};

	const handleBreakChange = (i, bi, field, value) => {
		setDays((prev) => {
			const updated = [...prev];
			const day = { ...updated[i] };
			const breaks = [...day.breaks];
			const brk = { ...breaks[bi] };

			const shiftStart = toMin(day.start);
			const shiftEnd = toMin(day.end);

			const newStart = field === "start" ? value : brk.start;
			const newEnd = field === "end" ? value : brk.end;

			const newStartMin = toMin(newStart);
			const newEndMin = toMin(newEnd);

			// ❌ must be inside shift
			if (newStartMin <= shiftStart || newEndMin >= shiftEnd) {
				return prev;
			}

			// ❌ break must be start < end
			if (newStartMin >= newEndMin) {
				return prev;
			}

			// ✅ accept change
			brk[field] = value;
			breaks[bi] = brk;
			day.breaks = breaks;
			updated[i] = day;

			return updated;
		});
	};

	const removeBreak = (i, bi) => {
		setDays((prev) => prev.map((d, idx) => (idx === i ? { ...d, breaks: d.breaks.filter((_, x) => x !== bi) } : d)));
	};

	const toggleExpand = (dayEN, open) => {
		if (!open) return; // cannot expand closed days
		setExpandedDay(expandedDay === dayEN ? null : dayEN);
	};

	const handleSave = async () => {
		if (!selectedScheduleId) return;

		// make sure the schedule currently on screen is included, even if the
		// user never switched away from it
		scheduleEditsRef.current[selectedScheduleId] = days;

		const buildPayload = (editedDays) => ({
			days: editedDays
				.filter((d) => d.open)
				.map((d) => ({
					dayEN: d.dayEN,
					dayES: d.dayES,
					shifts: [{ start: d.start, end: d.end }],
					breaks: d.breaks.map((b) => ({ start: b.start, end: b.end })),
				})),
		});

		try {
			setSaving(true);

			let res;
			// sequential, not Promise.all — each PUT returns the full shop
			// document, so awaiting in order keeps the last response accurate
			for (const [scheduleId, editedDays] of Object.entries(scheduleEditsRef.current)) {
				res = await scheduleBasedRequest.updateSchedule(scheduleId, buildPayload(editedDays), userData.cognitoTokens.AccessToken);
			}

			scheduleEditsRef.current = {};

			if (role === "admin" || role === "owner") {
				setUserData((prev) => ({ ...prev, shop: res.shop }));
			}

			if (role === "staff") {
				setUserData((prev) => {
					const onboarding = prev?.user?.onboarding || {};
					const currentSteps = onboarding.steps;

					// 🚫 If no steps → do NOTHING at all
					if (!currentSteps) return prev;

					// Only update if steps exist
					const steps = { ...currentSteps };

					if ("schedule" in steps) {
						steps.schedule = true;
					}

					const required = onboarding.requiredSteps || [];
					const isComplete = required.every((step) => steps[step]);

					return {
						...prev,
						user: {
							...prev.user,
							onboarding: {
								...onboarding,
								steps, // only included because it already existed
								isComplete,
								lastUpdatedAt: new Date().toISOString(),
							},
						},
					};
				});
			}

			onClose();
		} catch (err) {
			console.error("Error saving schedule:", err);
		} finally {
			setSaving(false);
		}
	};

	const loadSchedule = (staff) => {
    if (!staff?.week) return;

    // Map each week entry to its English weekday, derived from the date key
    const byDayName = {};
    Object.entries(staff.week).forEach(([dateStr, entry]) => {
        const dayEN = dayjs(dateStr).locale("en").format("dddd"); // "Monday"
        byDayName[dayEN] = entry;
    });

    const mappedDays = defaultDays.map((templateDay) => {
        const entry = byDayName[templateDay.dayEN];
        const firstShift = entry?.shifts?.[0];

        return fitBreaksToDay({
            ...templateDay,
            open: (entry?.shifts?.length || 0) > 0,
            start: firstShift?.start || templateDay.start,
            end: firstShift?.end || templateDay.end,
            breaks: entry?.breaks || [],
        });
    });

    setDays(mappedDays);
};

	// The bars are drawn against the week's own span rather than a fixed
	// 24h, so a 9–5 shop uses the full width instead of a third of it.
	const openDays = days.filter((d) => d.open);
	const weekStartMin = openDays.length ? Math.min(...openDays.map((d) => toMin(d.start))) : 9 * 60;
	const weekEndMin = openDays.length ? Math.max(...openDays.map((d) => toMin(d.end))) : 17 * 60;
	const weekSpan = Math.max(weekEndMin - weekStartMin, 60);
	const pct = (minutes) => Math.min(100, Math.max(0, ((minutes - weekStartMin) / weekSpan) * 100));

	function formatTimeDisplay(time) {
		if (!time) return "";

		const [h, m] = time.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = String(m).padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		return `${hour12}:${minutes} ${period}`;
	}

	return (
		<div className="sf-base-overlay">
			<div className="sf-base-modal">
				{/* HEADER */}
				<div className="sf-base-header">
					<h2>{translations[language].calendar.editor.title}</h2>
					<button className="sf-base-close" onClick={onClose}>
						✕
					</button>
				</div>

				{staffList.length > 1 && (
					<div className="sf-sdd-div">
							<StaffDropdown
								staffList={staffList}
								selectedStaffId={selectedStaffId || ""}
								onSelect={(id) => {
									const found = staffList.find((s) => s.staffId === id);
									if (found) {
										// stash the outgoing staff's edits so they survive the switch
										if (selectedScheduleId) {
											scheduleEditsRef.current[selectedScheduleId] = days;
										}

										setSelectedStaffId(found.staffId);
										setSelectedScheduleId(found.scheduleId);
										// let the calendar remember who we're on, so reopening lands here too
										if (onStaffChange) onStaffChange(found.staffId);

										const cached = scheduleEditsRef.current[found.scheduleId];
										if (cached) {
											setDays(cached);
										} else {
											loadSchedule(found);
										}
									}
									setStaffDropdownOpen(false);
								}}
								language={language}
								open={staffDropdownOpen}
								onToggle={() => setStaffDropdownOpen((p) => !p)}
								showAnyOption={false}
							/>
					</div>
				)}

				{/* BODY */}
				<div className="sf-base-body">
					{/* the bars are relative to the week's span, so say the range once */}
					<div className="sf-base-axis">
						<span>{formatTimeDisplay(toTime(weekStartMin))}</span>
						<i />
						<span>{formatTimeDisplay(toTime(weekEndMin))}</span>
					</div>

					{days.map((d, i) => {
						const dayLabel = language === "es" ? d.dayES : d.dayEN;
						const expanded = expandedDay === d.dayEN;

						return (
							<div key={i} className="sf-week-item">
								{/* ROW HEADER */}
								<div className="sf-week-header" onClick={() => toggleExpand(d.dayEN, d.open)}>
									<input
										type="checkbox"
										className="sf-toggle"
										checked={d.open}
										onChange={() => toggleOpen(i)}
										onClick={(e) => e.stopPropagation()}
									/>

									<span className="sf-week-day">{dayLabel}</span>

									{d.open ? (
										<span className="sf-week-hours">
											{formatTimeDisplay(d.start)} – {formatTimeDisplay(d.end)}
											{d.breaks.length > 0 && (
												<span className="sf-break-count">
													{d.breaks.length} {translations[language].calendar.break}
													{d.breaks.length > 1 ? "s" : ""}
												</span>
											)}
										</span>
									) : (
										<span className="sf-week-closed">{translations[language].calendar.editor.closed}</span>
									)}

									{/* closed days can't expand, so they get no chevron to promise it */}
									{d.open && (
										<span className={`sf-chevron ${expanded ? "open" : ""}`}>
											<ChevronDown size={17} strokeWidth={2.2} />
										</span>
									)}

									{/* the week's shape: working window, with breaks punched out */}
									<div className="sf-week-bar" aria-hidden="true">
										{d.open && (
											<>
												<span
													className="sf-week-bar-fill"
													style={{
														left: `${pct(toMin(d.start))}%`,
														width: `${pct(toMin(d.end)) - pct(toMin(d.start))}%`,
													}}
												/>
												{d.breaks.map((b, bi) => (
													<span
														key={bi}
														className="sf-week-bar-gap"
														style={{
															left: `${pct(toMin(b.start))}%`,
															width: `${pct(toMin(b.end)) - pct(toMin(b.start))}%`,
														}}
													/>
												))}
											</>
										)}
									</div>
								</div>

								{/* EXPANDED CARD */}
								{expanded && d.open && (
									<div className="sf-day-card">
										{/* HOURS */}
										<div className="sf-section">
											<div className="sf-section-label">{translations[language].calendar.editor.hours}</div>
											<div className="sf-time-row">
												<select value={d.start} onChange={(e) => handleTimeChange(i, "start", e.target.value)}>
													{times.map((t) => (
														<option key={t} value={t}>
															{formatTimeDisplay(t)}
														</option>
													))}
												</select>

												<span className="sf-dash">—</span>

												<select value={d.end} onChange={(e) => handleTimeChange(i, "end", e.target.value)}>
													{times
														.filter((t) => t > d.start)
														.map((t) => (
															<option key={t} value={t}>
																{formatTimeDisplay(t)}
															</option>
														))}
												</select>
											</div>
										</div>

										{/* BREAKS */}
										<div className="sf-section">
											<div className="sf-section-label">{translations[language].calendar.editor.breaks}</div>

											{d.breaks.map((b, bi) => (
												<div key={bi} className="sf-break-item">
													<div className="sf-break-row">
														<select value={b.start} onChange={(e) => handleBreakChange(i, bi, "start", e.target.value)}>
															{times
																.filter((t) => t > d.start && t < b.end)
																.map((t) => (
																	<option key={t} value={t}>
																		{formatTimeDisplay(t)}
																	</option>
																))}
														</select>

														<span className="sf-dash">—</span>

														<select value={b.end} onChange={(e) => handleBreakChange(i, bi, "end", e.target.value)}>
															{times
																.filter((t) => t > b.start && t < d.end)
																.map((t) => (
																	<option key={t} value={t}>
																		{formatTimeDisplay(t)}
																	</option>
																))}
														</select>
													</div>

													<button className="sf-break-delete" onClick={() => removeBreak(i, bi)}>
														<Trash2 size={20} strokeWidth={1.8} />
													</button>
												</div>
											))}

											<button className="sf-add-break" onClick={() => addBreak(i)} disabled={!nextBreakSlot(d)}>
												{translations[language].calendar.breaks.add}
											</button>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>

				{/* FOOTER */}
				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default BaseScheduleModal;
